import { MapUtils } from '../../utility/map-utils.js';
import {
  cloneData,
  cloneDataOrNull,
  DataModel,
  DataModelBuilder,
  isSameData,
  SerializationContext,
  SerializedData,
} from '../dto/dto.js';
import { VersionRange } from '../core/version-range.js';
import { PaymentAccount } from './payment-account.js';
import { PaymentProtocol } from './payment-protocol.js';

export class PaymentProtocolSettings extends DataModel {
  static readonly TYPE_ID: string = '39b4add95d114b499fb1217c1124238c';
  static readonly SCHEMA_VERSION: number = 3;

  private constructor(
    public readonly standard: PaymentProtocol,
    public readonly products: PaymentProtocol | null,
    public readonly donations: PaymentProtocol | null,
  ) {
    super();
  }

  get $typeId() {
    return PaymentProtocolSettings.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, PaymentProtocolSettings.SCHEMA_VERSION);
  }

  static create(args: {
    standard: PaymentProtocol,
    products: PaymentProtocol | null,
    donations: PaymentProtocol | null,
  }): PaymentProtocolSettings {
    return new PaymentProtocolSettings(
        args.standard,
        args.products,
        args.donations,
    );
  }

  static fromPaymentAccounts(args: {
    standard: PaymentAccount,
    products: PaymentAccount | null,
    donations: PaymentAccount | null,
  }): PaymentProtocolSettings {
    return new PaymentProtocolSettings(
        PaymentProtocol.fromPaymentAccount(args.standard),
        args.products != null ? PaymentProtocol.fromPaymentAccount(args.products) : null,
        args.donations != null ? PaymentProtocol.fromPaymentAccount(args.donations) : null,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): PaymentProtocolSettings {
    return new PaymentProtocolSettings(
        context.fromData(MapUtils.getRecord(map, 'standard')),
        context.fromDataOrNull<PaymentProtocol>(MapUtils.getRecordOrNull(map, 'products')),
        context.fromDataOrNull<PaymentProtocol>(MapUtils.getRecordOrNull(map, 'donations')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'standard': context.toData(this.standard),
      'products': context.toDataOrNull(this.products),
      'donations': context.toDataOrNull(this.donations),
    };
  }

  clone(): this {
    return new PaymentProtocolSettings(
        this.standard.clone(),
        this.products?.clone() ?? null,
        this.donations?.clone() ?? null,
    ) as this;
  }

  copyWith({
    standard = cloneData(this.standard),
    products = cloneDataOrNull(this.products),
    donations = cloneDataOrNull(this.donations),
  }: {
    standard?: PaymentProtocol,
    products?: PaymentProtocol | null,
    donations?: PaymentProtocol | null,
  } = {}): this {
    return PaymentProtocolSettings.create({
      standard,
      products,
      donations,
    }) as this;
  }

  isSame(other: PaymentProtocolSettings): boolean {
    if(this === other) {
      return true;
    }

    return isSameData(other.standard, this.standard) &&
        isSameData(other.products, this.products) &&
        isSameData(other.donations, this.donations);
  }
}

export class PaymentProtocolSettingsBuilder extends DataModelBuilder<PaymentProtocolSettings> {
  build(context: SerializationContext, map: SerializedData): PaymentProtocolSettings {
    return PaymentProtocolSettings.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(PaymentProtocolSettings.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(PaymentProtocolSettings.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version the [products] field was added.
    if(version < 2) {
      version = 2;
      map['products'] = null;
    }

    // In this version the [donations] field was added.
    if(version < 3) {
      version = 3;
      map['donations'] = null;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
